import React, { useState } from 'react';
import classNames from 'classnames';
import { Checkbox } from 'antd';
import { Tooltip } from 'components';
import Search from '../../../../components/search/search';
import { CheckAll, TeamRow } from './search-items';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';

export interface Props {
	allTeams: Team[];
	selectedTeams: SelectedTeams;
	setSelectedTeams: (selectedTeams: SelectedTeams) => void;
	isMerchantLevel: boolean;
	disabledTeamIds?: Set<string>;
	conflictRulesByTeamId: Map<string, string[]>;
	onTeamLevelToggle: (checked: boolean) => void;
	translations: Translations;
	readOnly?: boolean;
}

export interface Team {
	id: string;
	name: string;
}

export interface SelectedTeams {
	[id: string]: boolean;
}

export interface Translations {
	selectAll: string;
	title: string;
	teamSectionTooltip: string;
	setToSpecificTeams: string;
	description: string;
	showSelected: string;
	searchPlaceholder: string;
	disabledTeamTranslation: string;
	teamsAssignedToRules: string;
}

const checkAllId = 'check-all';

const TeamsSection: React.FC<Props> = ({
	allTeams,
	selectedTeams,
	setSelectedTeams,
	isMerchantLevel,
	disabledTeamIds = new Set(),
	conflictRulesByTeamId,
	onTeamLevelToggle,
	translations,
	readOnly
}) => {
	const [showOnlySelected, setShowOnlySelected] = useState(false);

	const onToggleTeam = (teamId, value) => {
		setSelectedTeams({ ...selectedTeams, [teamId]: value });
	};

	const items = React.useMemo(() => {
		const filteredTeams = allTeams.filter(item => {
			if (showOnlySelected) {
				return selectedTeams[item.id];
			}
			return true;
		});
		const teams = filteredTeams.map(team => ({ id: team.id, value: team.name }));
		return [{ id: checkAllId, value: `(${translations.selectAll})` }, ...teams];
	}, [showOnlySelected, allTeams, selectedTeams, translations]);

	const onMerchantLevelChange = e => {
		onTeamLevelToggle(e.target.checked);
	};

	return (
		<div className="team-section">
			<div className="is-merchant-level">
				<Checkbox
					className="is-merchant-level-checkbox"
					checked={isMerchantLevel}
					onChange={onMerchantLevelChange}
					disabled={readOnly}
				/>
				<span> {translations.setToSpecificTeams} </span>
				<Tooltip placement="top" title={translations.teamSectionTooltip}>
					<span className="team-section-info-button">
						<BringgIcon iconName={BringgFontIcons.Help} />
					</span>
				</Tooltip>
			</div>
			<div className={classNames('team-level-wrapper', { disabled: isMerchantLevel })}>
				<div className="team-level">
					<div className="team-level-title">{translations.title}</div>
					<div className="team-section-description">{translations.description}</div>
					<div className="show-selected">
						<Checkbox
							className="team-section-checkbox"
							checked={showOnlySelected}
							onChange={e => {
								setShowOnlySelected(e.target.checked);
							}}
							disabled={readOnly}
						/>
						<span>{translations.showSelected}</span>
					</div>
					<div className="search-box">
						<Search
							items={items}
							translations={{
								searchPlaceholder: translations.searchPlaceholder
							}}
							renderItem={(item, element) =>
								item.id === checkAllId ? (
									<CheckAll
										key={item.id}
										element={element}
										setSelectedTeams={setSelectedTeams}
										selectedTeams={selectedTeams}
										allTeams={allTeams}
										disabledTeamIds={disabledTeamIds}
										disabled={readOnly}
									/>
								) : (
									<TeamRow
										key={item.id}
										element={element}
										disabled={disabledTeamIds?.has(item.id)}
										conflictRules={conflictRulesByTeamId.get(item.id) || []}
										disabledTeamTranslation={translations.disabledTeamTranslation}
										checked={selectedTeams[item.id]}
										readOnly={readOnly}
										onChange={e => {
											onToggleTeam(item.id, e.target.checked);
										}}
									/>
								)
							}
						/>
					</div>
					{conflictRulesByTeamId.size > 0 && (
						<div className="indicators">
							<span className="indicator" />
							<span className="indicator-text">{translations.teamsAssignedToRules}</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default TeamsSection;
